import React, { useState, useEffect } from 'react';
import { UserTier } from '../../types';

interface User {
  id: string;
  decryptedEmail: string;
  emailHash?: string;
  encryptedEmail?: string;
  encryptedEmailHash?: string;
  createdAt: number;
  tier: UserTier;
  currentHourMessages: number;
}

interface ModalBackdropProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

const UserManagementTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [selectedUserForTier, setSelectedUserForTier] = useState<User | null>(null);
  const [newTier, setNewTier] = useState<UserTier>('free');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const getEmailHash = (user: User): string => {
    return user.encryptedEmailHash || user.emailHash || user.encryptedEmail || user.id || '';
  };

  const truncateHash = (hash: string, length: number = 16) => {
    if (!hash) return '';
    return hash.length > length ? `${hash.substring(0, length)}...` : hash;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await Promise.all(selectedUsers.map(userId =>
        fetch(`${import.meta.env.VITE_WORKER_URL}/admin/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
          }
        })
      ));
      
      await fetchUsers();
      setSelectedUsers([]);
      setShowConfirmDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleUpdateTier = async (userId: string, tier: UserTier) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/users/${userId}/tier`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier })
      });

      if (!response.ok) throw new Error('Failed to update tier');
      
      await fetchUsers();
      setShowTierDialog(false);
      setSelectedUserForTier(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Modal backdrop component
  const ModalBackdrop: React.FC<ModalBackdropProps> = ({ children, isOpen, onClose }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div 
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <div className="relative z-50">
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-none mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-100">User Management</h1>
        <button
          onClick={() => setShowConfirmDialog(true)}
          disabled={selectedUsers.length === 0}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Delete Selected Users
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-md text-red-500">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="flex-1 bg-slate-800 rounded-lg shadow overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(users.map(u => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                      className="rounded border-slate-600"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">User ID</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Encrypted Email Hash</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Created At</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Tier</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Messages This Hour</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-700/50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                          }
                        }}
                        className="rounded border-slate-600"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-200">{user.decryptedEmail}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center group">
                        <span 
                          className="font-mono text-emerald-400 text-xs cursor-pointer hover:text-emerald-300 transition-colors"
                          title={user.id}
                          onClick={() => copyToClipboard(user.id)}
                        >
                          {truncateHash(user.id, 24)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(user.id)}
                          className="ml-2 p-1 text-slate-500 hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Copy user ID"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center group">
                        <span 
                          className="font-mono text-slate-400 text-xs cursor-pointer hover:text-slate-200 transition-colors"
                          title={getEmailHash(user)}
                          onClick={() => copyToClipboard(getEmailHash(user))}
                        >
                          {truncateHash(getEmailHash(user))}
                        </span>
                        <button
                          onClick={() => copyToClipboard(getEmailHash(user))}
                          className="ml-2 p-1 text-slate-500 hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Copy full hash"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => {
                          setSelectedUserForTier(user);
                          setNewTier(user.tier);
                          setShowTierDialog(true);
                        }}
                        className={`px-2 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity ${
                          user.tier === 'alpha' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}
                      >
                        {user.tier}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {user.currentHourMessages}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ModalBackdrop isOpen={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 border border-slate-700">
          <h3 className="text-lg font-semibold text-slate-100 mb-2">
            Confirm Deletion
          </h3>
          <p className="text-slate-300 mb-6">
            Are you sure you want to delete {selectedUsers.length} selected user(s)?
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowConfirmDialog(false)}
              className="px-4 py-2 rounded-md bg-slate-700 text-slate-100 hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </ModalBackdrop>

      {/* Tier Update Modal */}
      <ModalBackdrop isOpen={showTierDialog} onClose={() => setShowTierDialog(false)}>
        <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 border border-slate-700">
          <h3 className="text-lg font-semibold text-slate-100 mb-2">
            Update User Tier
          </h3>
          {selectedUserForTier && (
            <div className="space-y-4">
              <p className="text-slate-300">Update tier for user: {selectedUserForTier.decryptedEmail}</p>
              <select
                value={newTier}
                onChange={(e) => setNewTier(e.target.value as UserTier)}
                className="w-full p-2 rounded-md bg-slate-700 text-slate-100 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="free">Free</option>
                <option value="alpha">Alpha</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowTierDialog(false)}
              className="px-4 py-2 rounded-md bg-slate-700 text-slate-100 hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={() => selectedUserForTier && handleUpdateTier(selectedUserForTier.id, newTier)}
              className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Update Tier
            </button>
          </div>
        </div>
      </ModalBackdrop>
    </div>
  );
};

export default UserManagementTab;
